"use client";

import { useState } from "react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { useToast } from "@/hooks/use-toast";
import { Order } from "@/types/orders/orders";

interface SaveOrderDialogProps {
  order: Order;
  isSubmitting: boolean;
  hasChanges: boolean;
  onSave: (sendEmail: boolean, emailMessage?: string) => Promise<void>;
}

export function SaveOrderDialog({
  order,
  isSubmitting,
  hasChanges,
  onSave,
}: SaveOrderDialogProps) {
  const [open, setOpen] = useState(false);
  const [sendEmail, setSendEmail] = useState(false);
  const [emailMessage, setEmailMessage] = useState("");
  const { toast } = useToast();

  const handleSave = async () => {
    try {
      await onSave(sendEmail, sendEmail ? emailMessage.trim() : undefined);
      setOpen(false);
      setEmailMessage("");
      setSendEmail(false);
    } catch (error) {
      console.error("Error saving order:", error);
      toast({
        title: "Save failed",
        description:
          error instanceof Error
            ? error.message
            : "Failed to save the order. Please try again.",
        variant: "destructive",
      });
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button disabled={isSubmitting || !hasChanges}>
          {isSubmitting ? "Saving..." : "Save Changes"}
        </Button>
      </DialogTrigger>
      <DialogContent className='sm:max-w-[500px]'>
        <DialogHeader>
          <DialogTitle>Save Order Changes</DialogTitle>
          <DialogDescription>
            Save changes to Order {order.order_number}
          </DialogDescription>
        </DialogHeader>

        <div className='grid gap-4 py-4'>
          <div className='flex items-center space-x-2'>
            <Checkbox
              id='send-email'
              checked={sendEmail}
              onCheckedChange={(checked) => setSendEmail(checked as boolean)}
            />
            <Label htmlFor='send-email' className='cursor-pointer'>
              Send update email to customer
            </Label>
          </div>

          {sendEmail && (
            <div className='grid gap-2'>
              <Label htmlFor='update-message'>Update Message (optional)</Label>
              <Textarea
                id='update-message'
                placeholder='Enter a message explaining the update to your customer...'
                value={emailMessage}
                onChange={(e) => setEmailMessage(e.target.value)}
                className='min-h-[100px]'
              />
              <p className='text-sm text-muted-foreground'>
                The email will include the current order status and details. Use
                this field to add any additional context.
              </p>
            </div>
          )}
        </div>

        <DialogFooter>
          <Button
            variant='outline'
            onClick={() => setOpen(false)}
            disabled={isSubmitting}
          >
            Cancel
          </Button>
          <Button onClick={handleSave} disabled={isSubmitting}>
            {isSubmitting ? "Saving..." : "Save Changes"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
